/**
 * Module-level token store.
 *
 * Access token lives only in memory (not localStorage) so it cannot be
 * stolen by XSS. Refresh token goes to localStorage so sessions survive
 * page reloads; it is only ever sent over HTTPS to our own backend.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── In-memory access token ────────────────────────────────────────────────────

let _accessToken = null;

export function getAccessToken()        { return _accessToken; }
export function setAccessToken(token)   { _accessToken = token; }
export function clearAccessToken()      { _accessToken = null; }

// ── Persisted refresh token ───────────────────────────────────────────────────

const REFRESH_KEY = 'inv_rt';

export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
export function setRefreshToken(token) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else        localStorage.removeItem(REFRESH_KEY);
  } catch {}
}
export function clearRefreshToken() {
  try { localStorage.removeItem(REFRESH_KEY); } catch {}
}

// ── JWT payload decoder ───────────────────────────────────────────────────────

export function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

// ── Startup guard ─────────────────────────────────────────────────────────────
// authFetch waits for this before making a request when there is no access
// token in memory, so it never races with AuthContext.initAuth for the
// refresh token.

let _authInitResolve = null;
export const authInitPromise = new Promise(resolve => { _authInitResolve = resolve; });

export function markAuthInitComplete() {
  if (_authInitResolve) { _authInitResolve(); _authInitResolve = null; }
}

// ── Singleton token refresh ───────────────────────────────────────────────────
// The ONE place that calls /api/auth/refresh. All callers (AuthContext timer,
// AuthContext initAuth, authFetch on 401) go through here. The singleton
// promise deduplicates concurrent calls so only one network request is made
// even if multiple code paths trigger a refresh simultaneously.

let _refreshPromise = null;

/**
 * Exchange the stored refresh token for a fresh access + refresh token pair.
 * Returns the response data object on success, or null on any failure.
 * Concurrent callers share the same in-flight promise.
 */
export async function refreshTokens() {
  // Return the in-flight promise if a refresh is already in progress.
  if (_refreshPromise) return _refreshPromise;

  const rt = getRefreshToken();
  if (!rt) return null;

  _refreshPromise = fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
  })
    .then(async res => {
      if (!res.ok) {
        // Refresh token is invalid/expired — clear stored credentials.
        clearAccessToken();
        clearRefreshToken();
        return null;
      }
      const data = await res.json();
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      return data;
    })
    .catch(() => null)
    .finally(() => { _refreshPromise = null; });

  return _refreshPromise;
}
